import { useEffect, useMemo, useState } from 'react';
import { Link, useRoute, useSearch } from 'wouter';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { useActivationEvent } from '@/hooks/useActivationEvents';
import { useActivations } from '@/hooks/useActivations';
import { OperatorCapturePanel } from '@/components/activations/OperatorCapturePanel';
import { ApprovalReviewPanel } from '@/components/activations/ApprovalReviewPanel';
import { BlazePodScorePanel } from '@/components/activations/BlazePodScorePanel';
import { ACTIVATION_TYPE_LABELS, asActivationType, readConfigValues } from '@/types/activations';

/**
 * Operator console for operator-driven activations. Two kinds are surfaced here:
 *  - OperatorCurated photo booths — the operator photographs guests and generates assets
 *    (Capture tab); an Athlyn-team member reviews and approves them (Review tab).
 *  - BlazePod (speed-tap) — the operator records each registered guest's score.
 * Scoped to one event; the activation picker lists this event's operator-driven activations.
 */
export default function OperatorConsolePage() {
  const [, params] = useRoute('/activations/events/:eventId/operator');
  const eventId = params?.eventId;
  const search = useSearch();

  const { data: event } = useActivationEvent(eventId);
  const { data: activations } = useActivations(eventId);

  const operatorActivations = useMemo(
    () =>
      (activations ?? []).filter((a) => {
        // BlazePod activations are always operator-driven.
        if (asActivationType(a.activationType) === 'BlazePod') return true;
        // PhotoBooth is operator-driven only when its capture mode is OperatorCurated.
        if (a.activationType !== 'PhotoBooth') return false;
        const cfg = readConfigValues(a.config) as Record<string, unknown> | undefined;
        return cfg?.captureMode === 'OperatorCurated';
      }),
    [activations],
  );

  const initialActivationId = useMemo(
    () => new URLSearchParams(search).get('activationId') ?? '',
    [search],
  );
  const [selectedActivationId, setSelectedActivationId] = useState<string>(initialActivationId);

  // Default to the first operator activation once loaded (if none came in the URL).
  useEffect(() => {
    if (!selectedActivationId && operatorActivations.length > 0) {
      setSelectedActivationId(operatorActivations[0].id);
    }
  }, [operatorActivations, selectedActivationId]);

  const selectedActivation = useMemo(
    () => operatorActivations.find((a) => a.id === selectedActivationId) ?? null,
    [operatorActivations, selectedActivationId],
  );
  const isBlazePod =
    !!selectedActivation && asActivationType(selectedActivation.activationType) === 'BlazePod';

  return (
    <div className="flex-1" data-testid="operator-console-page">
      <Header
        title={event ? `Operator console — ${event.name}` : 'Operator console'}
        description="Photograph registered guests, generate assets, and review approvals."
      >
        <Button variant="outline" asChild>
          <Link href={`/activations/events/${eventId}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to event
          </Link>
        </Button>
      </Header>

      <div className="space-y-6 p-6">
        <div className="max-w-sm">
          <Select value={selectedActivationId} onValueChange={setSelectedActivationId}>
            <SelectTrigger data-testid="operator-activation-picker">
              <SelectValue placeholder="Select an activation…" />
            </SelectTrigger>
            <SelectContent>
              {operatorActivations.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {ACTIVATION_TYPE_LABELS[a.activationType]} · {a.shortCode}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!selectedActivationId ? (
          <Card className="flex flex-col items-center gap-2 p-12 text-center" data-testid="operator-no-selection">
            <p className="text-sm text-muted-foreground">
              {operatorActivations.length === 0
                ? 'No operator-driven activations on this event. Attach a BlazePod activation, or set a photo booth’s capture mode to Operator-curated.'
                : 'Select an activation to start.'}
            </p>
          </Card>
        ) : isBlazePod ? (
          <BlazePodScorePanel activationId={selectedActivationId} />
        ) : (
          <Tabs defaultValue="capture">
            <TabsList>
              <TabsTrigger value="capture" data-testid="operator-tab-capture">Capture</TabsTrigger>
              <TabsTrigger value="review" data-testid="operator-tab-review">Review &amp; approve</TabsTrigger>
            </TabsList>
            <TabsContent value="capture" className="pt-4">
              <OperatorCapturePanel activationId={selectedActivationId} />
            </TabsContent>
            <TabsContent value="review" className="pt-4">
              <ApprovalReviewPanel activationId={selectedActivationId} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
